import React, { FC } from 'react';
import { Text, View, Image, Svg, Polygon, Path, Canvas } from '@react-pdf/renderer';
import { filterSquadKvRows, splitSquadKvDash, squadKeepNumberWithUnit } from './squadKvRows';
import { parseSquadInlineText } from './squadInlineText';
import { SQUAD_COLORS } from './templateConstants';
import { SQUAD_SECTION_GEOMETRY_PDF, SQUAD_TYPOGRAPHY_PDF } from './pageHeaderTokens';
import { kalenderjahrHasData, normalizeKalenderjahrData } from './kalenderjahr';
import {
  formatPortfolioPercent,
  formatSquadPiePercent,
  squadPieValueColumnPt,
  generateGradientColors,
  shouldShowBarValueInside,
  SQUAD_TABLE_BORDER_BOTTOM,
  SQUAD_TABLE_BORDER_TOP,
  SQUAD_TABLE_BORDER_WIDTH_PDF_PT,
  SQUAD_TABLE_COLUMN_GAP_PDF_PT,
  SQUAD_TABLE_FONT_SIZE_PDF_PT,
  SQUAD_TABLE_ROW_PAD_Y_PDF_PT,
  SQUAD_TABLE_TEXT_COLOR,
  SQUAD_PIE_BLOCK_GAP_PDF_PT,
  SQUAD_PIE_LEGEND_TOP_OFFSET_PDF_PT,
  SQUAD_SECTION_CONTENT_GAP_PDF_PT,
  squadPieLabelLiftPt,
  squadPieLegendAlign,
  type SquadPieLegendAlign,
} from './squadPortfolioChartUtils';
import { SQUAD_MIFID_NOTICE_WIDTH_PCT } from './SquadMifidOverlayLayer';
import { squadDonutSegments, squadDonutSeparators, SQUAD_VECTOR_PIE } from './squadVectorDonut';
import {
  SQUAD_DONUT_LABEL_COLOR_DARK,
  SQUAD_DONUT_LABEL_TOP_EM_PDF,
  squadDonutLabelLayout,
  squadDonutTextWidth,
  squadPieVariant,
  squadSectionPieReserve,
} from './squadDonutLabels';
import { squadRasterPieSlots, type SquadPortfolioBlock, type SquadPortfolioSection } from './squadPortfolioSections';

/**
 * Long plain-text field with the template's inline notation: `**bold**` and manual
 * line breaks. The Monatskommentar arrives this way from the Portfolio-Upload
 * (Kommentar A5) and the config field advertises the notation, but the PDF used to
 * strip the markers while the web preview rendered them — so the PDF silently lost
 * every emphasis the customer set.
 */
export const InlineProseText: FC<{ text?: string; style?: any }> = ({ text, style }) => {
  const lines = parseSquadInlineText(text);
  if (!lines.length) return null;
  return (
    <Text style={style}>
      {lines.map((line, lineIndex) => (
        <Text key={lineIndex}>
          {lineIndex > 0 ? '\n' : ''}
          {line.map((segment, segmentIndex) => (
            <Text
              key={segmentIndex}
              style={segment.bold ? { fontFamily: 'SegoeUI', fontWeight: 700 } : undefined}
            >
              {segment.text}
            </Text>
          ))}
        </Text>
      ))}
    </Text>
  );
};

/** Label mit hängendem Einzug bei führendem Spiegelstrich (splitSquadKvDash). */
const KvLabel: FC<{ label: string; styles: any }> = ({ label, styles }) => {
  const dash = splitSquadKvDash(label);
  if (!dash) return <Text style={styles.kvLabel}>{label}</Text>;
  return (
    <View style={{ flexDirection: 'row', width: '100%' }}>
      <Text style={{ ...styles.kvLabel, width: dash.columnEm * SQUAD_TYPOGRAPHY_PDF.kv, flexShrink: 0 }}>
        {dash.dash}
      </Text>
      <Text style={{ ...styles.kvLabel, flex: 1, width: undefined }}>{dash.rest}</Text>
    </View>
  );
};

export const KeyValueBlock: FC<{
  rows: Array<{ label: string; value: string }>;
  styles: any;
  /** Breite der Label-Spalte in % (Standard 60 wie im Original; Gebühren 55). */
  labelWidthPct?: number;
}> = ({ rows, styles, labelWidthPct }) => {
  const visible = filterSquadKvRows(rows || []);
  if (!visible.length) return null;
  return (
  <View>
    {visible.map((row, i) => (
      <View
        key={i}
        style={[
          styles.kvRow,
          i === visible.length - 1 ? { borderBottomWidth: 0 } : null,
        ]}
      >
        <View style={labelWidthPct ? { ...styles.kvLabelCell, width: `${labelWidthPct}%` } : styles.kvLabelCell}>
          <KvLabel label={row.label} styles={styles} />
        </View>
        <View style={labelWidthPct ? { ...styles.kvValueCell, width: `${100 - labelWidthPct}%` } : styles.kvValueCell}>
          <Text style={styles.kvValue}>
            {/* Line breaks and bold the customer placed — see squadInlineText */}
            {parseSquadInlineText(squadKeepNumberWithUnit(row.value)).map((line, lineIndex) => (
              <Text key={lineIndex}>
                {lineIndex > 0 ? '\n' : ''}
                {line.map((segment, segmentIndex) => (
                  <Text
                    key={segmentIndex}
                    style={segment.bold ? { fontFamily: 'SegoeUI', fontWeight: 700 } : undefined}
                  >
                    {segment.text}
                  </Text>
                ))}
              </Text>
            ))}
          </Text>
        </View>
      </View>
    ))}
  </View>
  );
};

export const HBars: FC<{ items: Array<{ label: string; value: number }>; styles: any; color: string }> = ({
  items,
  styles,
  color,
}) => {
  const peak = Math.max(...(items || []).map((i) => Number(i.value) || 0), 1);
  return (
    <View>
      {(items || []).map((item, idx) => {
        const value = Number(item.value) || 0;
        const pct = Math.max(0, Math.min(100, (value / peak) * 100));
        const formatted = formatPortfolioPercent(value);
        const valueInside = shouldShowBarValueInside(pct, formatted);
        return (
          <View key={idx} style={styles.portfolioBarRow}>
            <View style={styles.portfolioBarLabelCell}>
              <Text style={styles.portfolioBarLabel}>{item.label}</Text>
            </View>
            <View style={styles.portfolioBarTrack}>
              <View style={{ ...styles.portfolioBarFill, width: `${pct}%`, backgroundColor: color }}>
                {valueInside ? <Text style={styles.portfolioBarValueInside}>{formatted}</Text> : null}
              </View>
              {!valueInside ? <Text style={styles.portfolioBarValueOutside}>{formatted}</Text> : null}
            </View>
          </View>
        );
      })}
    </View>
  );
};

/**
 * PDF pie: captured donut (left) + native Text legend (right).
 * Chart is absolutely positioned so when the legend wraps to the next page
 * it keeps a left inset — as if the pie column were still there.
 */
export const PieChartBlock: FC<{
  imageSrc?: string;
  items: Array<{ label: string; value: number }>;
  primaryColor: string;
  /** Excel Farbtheme colors (widget-mode parity). Falls back to primary gradient. */
  colors?: string[];
  styles: any;
  placeholderLabel?: string;
}> = ({ imageSrc, items, primaryColor, colors: colorsProp, styles, placeholderLabel = 'Pie' }) => {
  const colors =
    Array.isArray(colorsProp) && colorsProp.length > 0
      ? colorsProp
      : generateGradientColors(primaryColor, Math.max(items?.length || 0, 1));
  const src = typeof imageSrc === 'string' ? imageSrc.trim() : '';

  return (
    <View style={styles.pieBlockRow}>
      <View style={styles.pieBlockChartCol} wrap={false}>
        {src ? (
          <Image src={src} style={styles.pieBlockChartImage} />
        ) : (
          <Placeholder label={placeholderLabel} styles={styles} height={100} />
        )}
      </View>
      <View style={styles.pieBlockLegendCol}>
        {(items || []).map((item, index) => (
          <View key={index} style={styles.pieLegendRow} wrap={false}>
            <View
              style={{
                ...styles.pieLegendMark,
                backgroundColor: colors[index % colors.length],
              }}
            />
            <Text style={styles.pieLegendLabel}>{item.label}</Text>
          </View>
        ))}
      </View>
    </View>
  );
};

/**
 * Donut as vector paths with the values in the legend — replaces the captured canvas
 * (PieChartBlock); since SQUAD chose E1 (15.10.2026) always this way. See squadVectorDonut.
 */
export const VectorPieChartBlock: FC<{
  items: Array<{ label: string; value: number }>;
  primaryColor: string;
  /** Excel Farbtheme colors (widget-mode parity). Falls back to primary gradient. */
  colors?: string[];
  styles: any;
  /** `pieChartStyle` des Dokuments — mit oder ohne Zahlen am Ring (squadDonutLabels). */
  pieStyle?: string;
  /** Gemeinsame Höhe über dem Ring für alle Torten eines Bereichs. */
  reserveTopPt?: number;
  /** Legende auf Ringmitte oder oben bündig mit der Nachbarspalte — squadPieLegendAlign. */
  legendAlign?: SquadPieLegendAlign;
}> = ({ items, primaryColor, colors: colorsProp, styles, pieStyle, reserveTopPt, legendAlign = 'ring' }) => {
  const colors =
    Array.isArray(colorsProp) && colorsProp.length > 0
      ? colorsProp
      : generateGradientColors(primaryColor, Math.max(items?.length || 0, 1));
  const values = (items || []).map((item) => Number(item.value));
  const valueColumnPt = squadPieValueColumnPt(values);
  const variant = squadPieVariant(pieStyle);
  const layout = squadDonutLabelLayout(values, colors, variant.mode, { ...variant.options, reserveTopPt });
  const segments = squadDonutSegments(values, colors, layout.geom, layout.startAngle);
  const separators = squadDonutSeparators(values, layout.geom, undefined, layout.startAngle);

  return (
    <View
      style={legendAlign === 'top' ? { ...styles.vectorPieRow, alignItems: 'flex-start' as const } : styles.vectorPieRow}
      wrap={false}
    >
      <View
        style={{
          position: 'relative',
          width: layout.width,
          height: layout.height,
          flexShrink: 0,
          marginTop: -squadPieLabelLiftPt(legendAlign, layout.topPt),
        }}
      >
        <Svg width={layout.width} height={layout.height} viewBox={`0 0 ${layout.width} ${layout.height}`}>
          {segments.map((s, k) => (
            <Path key={k} d={s.d} fill={s.color} />
          ))}
          {separators.map((d, k) => (
            <Path key={`sep-${k}`} d={d} fill="none" stroke="#FFFFFF" strokeWidth={SQUAD_VECTOR_PIE.separatorPt} />
          ))}
          {layout.leaders.map((d, k) => (
            <Path key={`lead-${k}`} d={d} fill="none" stroke={SQUAD_DONUT_LABEL_COLOR_DARK} strokeWidth={0.5} />
          ))}
        </Svg>
        {layout.labels.map((label, k) => {
          const w = squadDonutTextWidth(label.text, label.fontPt, label.bold) + 4;
          return (
            <Text
              key={`lbl-${k}`}
              style={{
                position: 'absolute',
                left: label.x - w / 2,
                top: label.y - label.fontPt * SQUAD_DONUT_LABEL_TOP_EM_PDF,
                width: w,
                textAlign: 'center',
                fontSize: label.fontPt,
                lineHeight: 1,
                color: label.color,
                fontFamily: 'SegoeUI',
                fontWeight: label.bold ? 700 : 400,
              }}
            >
              {label.text}
            </Text>
          );
        })}
      </View>
      {/* 'ring': um die Zeile über dem Ring versetzt → Legende auf Ringmitte. 'top': oben bündig. */}
      <View
        style={{
          ...styles.vectorPieLegend,
          marginTop: legendAlign === 'top' ? SQUAD_PIE_LEGEND_TOP_OFFSET_PDF_PT : layout.topPt,
        }}
      >
        {(items || []).map((item, index) => (
          <View key={index} style={styles.pieLegendRow} wrap={false}>
            <View
              style={{
                ...styles.pieLegendMark,
                backgroundColor: colors[index % colors.length],
              }}
            />
            {/* „■ 52% Aktien“ — Wert vor dem Text (SQUAD-Wahl E1, 15.10.2026) */}
            {variant.legendValues ? (
              <Text style={{ ...styles.pieLegendValue, width: valueColumnPt }}>
                {formatSquadPiePercent(Number(item.value))}
              </Text>
            ) : null}
            <Text style={styles.pieLegendLabel}>{item.label}</Text>
          </View>
        ))}
      </View>
    </View>
  );
};

/** Portfolio table (Renditen) — matches client custom-portfolio-table themeIndex 2. */
export const PortfolioTable: FC<{
  rows: Array<{ label: string; value: string }>;
  header?: { label: string; value: string };
}> = ({ rows, header }) => {
  const rowBase = {
    flexDirection: 'row' as const,
    alignItems: 'flex-start' as const,
    width: '100%' as const,
    paddingVertical: SQUAD_TABLE_ROW_PAD_Y_PDF_PT,
    borderBottomWidth: SQUAD_TABLE_BORDER_WIDTH_PDF_PT,
    borderBottomColor: SQUAD_TABLE_BORDER_BOTTOM,
    borderBottomStyle: 'solid' as const,
    gap: SQUAD_TABLE_COLUMN_GAP_PDF_PT,
  };
  const cell = {
    fontSize: SQUAD_TABLE_FONT_SIZE_PDF_PT,
    color: SQUAD_TABLE_TEXT_COLOR,
    lineHeight: 1.25,
  };
  // Value as wide as its text, flush right; the label takes what it and the gap leave (as on the web).
  const labelCell = { ...cell, flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0 };
  const valueCell = { ...cell, flexShrink: 0, textAlign: 'right' as const };
  return (
    <View>
      {header ? (
        <View
          style={{
            ...rowBase,
            borderTopWidth: SQUAD_TABLE_BORDER_WIDTH_PDF_PT,
            borderTopColor: SQUAD_TABLE_BORDER_TOP,
            borderTopStyle: 'solid',
            marginBottom: 1,
          }}
        >
          <Text style={{ ...labelCell, fontFamily: 'SegoeUI', fontWeight: 700 }}>
            {header.label}
          </Text>
          <Text style={{ ...valueCell, fontFamily: 'SegoeUI', fontWeight: 700 }}>
            {header.value}
          </Text>
        </View>
      ) : null}
      {(rows || []).map((row, i) => (
        <View
          key={i}
          style={{
            ...rowBase,
            ...(!header && i === 0
              ? {
                  borderTopWidth: SQUAD_TABLE_BORDER_WIDTH_PDF_PT,
                  borderTopColor: SQUAD_TABLE_BORDER_TOP,
                  borderTopStyle: 'solid' as const,
                }
              : null),
          }}
        >
          <Text style={labelCell}>{row.label}</Text>
          <Text style={valueCell}>{row.value}</Text>
        </View>
      ))}
    </View>
  );
};

/**
 * Unsichtbare, fixierte Sonde je logischer Seite: meldet, auf welchen physischen Seiten
 * sie landet (siehe squadPageLayout). Nur im Vorschaudialog aktiv — ohne `onLayout`
 * wird nichts gerendert, das veröffentlichte PDF bleibt unverändert.
 */
export const PageLayoutProbe: FC<{
  page: number;
  onLayout?: (logicalPage: number, pageNumber: number, totalPages: number) => void;
}> = ({ page, onLayout }) =>
  onLayout ? (
    <Text
      fixed
      style={{ position: 'absolute', top: 0, left: 0, fontSize: 1 }}
      render={({ pageNumber, totalPages }) => {
        if (totalPages) onLayout(page, pageNumber, totalPages);
        return '';
      }}
    />
  ) : null;

/**
 * Seite 2 rechts — Portfolio-Bereiche in Excel-Reihenfolge (siehe squadPortfolioSections).
 * Abstände je Bereichsart wie bisher: Balken gap 10, Torten im 50-%-Slot (gap 8),
 * Tabellen gap 12; gemischte Bereiche gap 10, oben ausgerichtet.
 */
export const SquadPortfolioSectionsPdf: FC<{
  sections: SquadPortfolioSection[];
  /** Aufgelöste Überschriften (für umgewandelte Altdokumente mit `headerKey`). */
  headers: Record<string, string>;
  styles: any;
  barColor: string;
  chartPrimary: string;
  rasterPies: boolean;
  /** Bild-Slots 1/2 im Modus „Bild (bisher)“ — die ersten beiden Torten. */
  pieImages: [string | undefined, string | undefined];
  /** `pieChartStyle` des Dokuments (Ring-Beschriftung der Vektor-Torten). */
  pieStyle?: string;
}> = ({ sections, headers, styles, barColor, chartPrimary, rasterPies, pieImages, pieStyle }) => {
  const rasterSlots = squadRasterPieSlots(sections);

  const renderBlock = (
    block: SquadPortfolioBlock,
    slotKey: string,
    reserveTopPt?: number,
    legendAlign?: SquadPieLegendAlign,
  ) => {
    if (block.kind === 'bar') return <HBars items={block.items} styles={styles} color={block.color || barColor} />;
    if (block.kind === 'table') return <PortfolioTable rows={block.rows} header={block.header} />;
    if (block.kind === 'pie') {
      const slot = rasterPies ? rasterSlots.get(slotKey) : undefined;
      return (
        <View style={{ paddingTop: SQUAD_PIE_BLOCK_GAP_PDF_PT }}>
          {slot ? (
            <PieChartBlock
              imageSrc={pieImages[slot - 1]}
              items={block.items}
              primaryColor={chartPrimary}
              colors={block.colors || []}
              styles={styles}
              placeholderLabel="Pie"
            />
          ) : (
            <VectorPieChartBlock
              items={block.items}
              primaryColor={chartPrimary}
              colors={block.colors || []}
              styles={styles}
              pieStyle={pieStyle}
              reserveTopPt={reserveTopPt}
              legendAlign={legendAlign}
            />
          )}
        </View>
      );
    }
    return null;
  };

  return (
    <>
      {sections.map((section, si) => {
        const title = section.title || (section.headerKey ? headers[section.headerKey] : '') || '';
        const kinds = new Set(section.blocks.filter((b) => b.kind !== 'empty').map((b) => b.kind));
        const onlyPies = kinds.size === 1 && kinds.has('pie');
        const onlyBars = kinds.size === 1 && kinds.has('bar');
        const onlyTables = kinds.size === 1 && kinds.has('table');
        const twoColumns = section.columns === 2;
        // Nebeneinander stehende Torten: gleiche Höhe über dem Ring, damit die Ringe fluchten.
        const pieReserve = twoColumns ? squadSectionPieReserve(section.blocks, pieStyle) : undefined;
        const legendAlign = squadPieLegendAlign();
        const rowStyle = {
          ...(onlyPies && twoColumns
            ? styles.pieChartsRow
            : {
                flexDirection: twoColumns ? ('row' as const) : ('column' as const),
                gap: onlyTables ? 12 : 10,
                ...(onlyBars || onlyTables ? {} : { alignItems: 'flex-start' as const }),
              }),
          paddingTop: SQUAD_SECTION_CONTENT_GAP_PDF_PT,
        };
        const cellStyle = onlyPies && twoColumns
          ? styles.pieChartSlot
          : twoColumns
            ? { flex: 1, minWidth: 0 }
            : { width: '100%' };
        return (
          <View key={si} style={styles.mainSection}>
            {title ? <Text style={styles.navyBar}>{title}</Text> : null}
            {section.blocks.length > 0 ? (
              <View style={rowStyle}>
                {section.blocks.map((block, bi) => (
                  <View key={bi} style={cellStyle}>
                    {renderBlock(block, `${si}:${bi}`, pieReserve, legendAlign)}
                  </View>
                ))}
              </View>
            ) : null}
            {section.footnote ? (
              <Text
                style={{
                  fontSize: SQUAD_TYPOGRAPHY_PDF.note,
                  color: SQUAD_COLORS.text,
                  marginTop: onlyPies ? 6 : 4,
                  ...(onlyBars ? {} : { lineHeight: 1.35 }),
                }}
              >
                {section.footnote}
              </Text>
            ) : null}
          </View>
        );
      })}
    </>
  );
};

export const Placeholder: FC<{
  label: string;
  styles: any;
  height?: number;
  src?: string;
  /** When true, scale by width only so the image keeps its natural aspect ratio. */
  preserveAspectRatio?: boolean;
}> = ({ label, styles, height = 60, src, preserveAspectRatio = false }) => {
  const imageSrc = typeof src === 'string' ? src.trim() : '';
  if (imageSrc) {
    if (preserveAspectRatio) {
      return <Image src={imageSrc} style={{ width: '100%' }} />;
    }
    return <Image src={imageSrc} style={{ width: '100%', height }} />;
  }
  return (
    <View style={{ ...styles.placeholder, height }}>
      <Text style={styles.placeholderText}>{label}</Text>
    </View>
  );
};

/** Fills its box edge to edge: the image area below and the probe on top of it. */
const PAGE3_IMAGE_LAYER = { position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0 };

/**
 * Page 3 right below Rollierend: the combined image in whatever height is left above the
 * foot note (see squadPage3Image). The box grows into that space; the image is drawn
 * into it at full width, or scaled down with its aspect kept when it is taller — top
 * left, so it lines up with the bars above. `contain` has to be explicit: react-pdf
 * defaults to `fill` and would stretch it to the box.
 *
 * An empty slot prints nothing: no placeholder box in a customer's factsheet.
 *
 * `onArea` is only passed by the preview dialog: a Canvas is the one react-pdf node that
 * is told its laid-out size, so it reports the box for the "image too tall" warning.
 */
export const SquadPage3ImagePdf: FC<{
  src?: string;
  onArea?: (widthPt: number, heightPt: number) => void;
}> = ({ src, onArea }) => {
  const imageSrc = typeof src === 'string' ? src.trim() : '';
  if (!imageSrc) return null;
  return (
    <View
      style={{
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        minHeight: 0,
        // Where a navy bar would start, so the bar drawn in the image sits on the same line
        marginTop: SQUAD_SECTION_GEOMETRY_PDF.navyBarMarginTop,
        position: 'relative',
      }}
    >
      <Image
        src={imageSrc}
        style={{ ...PAGE3_IMAGE_LAYER, objectFit: 'contain', objectPositionX: 0, objectPositionY: 0 }}
      />
      {onArea ? (
        <Canvas
          style={PAGE3_IMAGE_LAYER}
          paint={(_painter, availableWidth, availableHeight) => {
            onArea(availableWidth, availableHeight);
            return null;
          }}
        />
      ) : null}
    </View>
  );
};

export const SidebarSectionTitle: FC<{ children: React.ReactNode; styles: any; primary: string }> = ({
  children,
  styles,
  primary,
}) => <Text style={{ ...styles.sidebarSectionTitle, color: primary }}>{children}</Text>;

export const KalenderjahrTable: FC<{
  data?: unknown;
  styles: any;
}> = ({ data, styles }) => {
  const table = normalizeKalenderjahrData(data);
  if (!kalenderjahrHasData(table)) {
    return <Placeholder label="Kalenderjahr-Performance" styles={styles} height={48} />;
  }
  const borderColor = '#D0D0D0';
  const labelWidth = 52;
  return (
    <View>
      {table.values.map((row, ri) => {
        const isJahr = String(row[0] || '').toLowerCase().includes('jahr');
        return (
          <View
            key={ri}
            style={{
              flexDirection: 'row',
              borderTopWidth: ri === 0 ? 0.5 : 0,
              borderTopColor: borderColor,
              borderBottomWidth: 0.5,
              borderBottomColor: borderColor,
              alignItems: 'center',
            }}
          >
            <Text
              style={{
                ...styles.tableCell,
                width: labelWidth,
                fontFamily: 'SegoeUI', fontWeight: 700,
                textAlign: 'left',
              }}
            >
              {row[0] || ''}
            </Text>
            {row.slice(1).map((cell, ci) => (
              <Text
                key={ci}
                style={{
                  ...styles.tableCell,
                  flex: 1,
                  textAlign: 'center',
                  fontFamily: 'SegoeUI', fontWeight: isJahr ? 700 : 400,
                }}
              >
                {cell || ''}
              </Text>
            ))}
          </View>
        );
      })}
    </View>
  );
};

/** Period grid — same chrome as custom-portfolio tables (themeIndex 2 hairlines). */
export const PerformanceRiskTable: FC<{ perf: { labels?: string[]; rows?: string[][] }; styles: any }> = ({
  perf,
}) => {
  const rowBase = {
    flexDirection: 'row' as const,
    alignItems: 'flex-start' as const,
    width: '100%' as const,
    paddingVertical: SQUAD_TABLE_ROW_PAD_Y_PDF_PT,
    borderBottomWidth: SQUAD_TABLE_BORDER_WIDTH_PDF_PT,
    borderBottomColor: SQUAD_TABLE_BORDER_BOTTOM,
    borderBottomStyle: 'solid' as const,
  };
  const cell = {
    flex: 1,
    fontSize: SQUAD_TABLE_FONT_SIZE_PDF_PT,
    color: SQUAD_TABLE_TEXT_COLOR,
    lineHeight: 1.25,
  };
  return (
    <View>
      <View
        style={{
          ...rowBase,
          borderTopWidth: SQUAD_TABLE_BORDER_WIDTH_PDF_PT,
          borderTopColor: SQUAD_TABLE_BORDER_TOP,
          borderTopStyle: 'solid',
          marginBottom: 1,
        }}
      >
        {(perf.labels || []).map((l: string, i: number) => (
          <Text
            key={i}
            style={{
              ...cell,
              fontFamily: 'SegoeUI',
              fontWeight: 700,
              textAlign: i === 0 ? 'left' : 'center',
            }}
          >
            {l}
          </Text>
        ))}
      </View>
      {(perf.rows || []).map((row: string[], ri: number) => (
        <View key={ri} style={rowBase}>
          {row.map((cellText, ci) => (
            <Text
              key={ci}
              style={{
                ...cell,
                fontFamily: 'SegoeUI',
                fontWeight: 400,
                textAlign: ci === 0 ? 'left' : 'center',
              }}
            >
              {cellText}
            </Text>
          ))}
        </View>
      ))}
    </View>
  );
};

export const RiskIndicatorBlock: FC<{
  level: number;
  text: string;
  styles: any;
  primary: string;
  /** Fill of the active box — the fund colour; scale, frame and text stay `primary`. */
  activeFill?: string;
}> = ({ level, text, styles, primary, activeFill = primary }) => (
  <View>
    <View style={styles.riskScaleLabels}>
      <Text style={{ ...styles.riskScaleLabel, color: primary }}>Niedrigeres Risiko</Text>
      <Text style={{ ...styles.riskScaleLabel, color: primary }}>Höheres Risiko</Text>
    </View>
    <View style={styles.riskArrowBar}>
      <Svg width={7} height={9} viewBox="0 0 7 9">
        <Polygon points="0,4.5 7,0 7,9" fill={primary} />
      </Svg>
      <View style={{ ...styles.riskArrowLine, backgroundColor: primary }} />
      <Svg width={7} height={9} viewBox="0 0 7 9">
        <Polygon points="7,4.5 0,0 0,9" fill={primary} />
      </Svg>
    </View>
    <View style={{ ...styles.riskScaleLabels, marginBottom: 6 }}>
      <Text style={{ ...styles.riskScaleLabel, color: primary }}>Potentiell geringerer Ertrag</Text>
      <Text style={{ ...styles.riskScaleLabel, color: primary }}>Potentiell höherer Ertrag</Text>
    </View>
    <View style={styles.riskScaleBoxes}>
      {[1, 2, 3, 4, 5, 6, 7].map((n) => (
        <View
          key={n}
          style={{
            ...styles.riskBox,
            ...(n > 1 ? styles.riskBoxLinked : null),
            backgroundColor: n === level ? activeFill : SQUAD_COLORS.white,
          }}
        >
          <Text style={{ color: n === level ? SQUAD_COLORS.white : primary, fontSize: SQUAD_TYPOGRAPHY_PDF.riskBox }}>
            {n}
          </Text>
        </View>
      ))}
    </View>
    <Text style={{ fontSize: SQUAD_TYPOGRAPHY_PDF.riskText, lineHeight: 1.35, color: primary, marginTop: 6 }}>
      {text}
    </Text>
  </View>
);

/** Split hard Enter lines for react-pdf (lineHeight is unreliable inside one Text). */
export const FootnoteLines: FC<{ text?: string; style?: any }> = ({ text, style }) => {
  const raw = String(text || '');
  if (!raw.trim()) return null;
  const lines = raw.split(/\r?\n/);
  const { marginTop, marginBottom, margin, ...lineStyle } = style || {};
  return (
    <View style={{ marginTop, marginBottom, margin }}>
      {lines.map((line, i) => (
        <Text key={i} style={lineStyle}>
          {line.length ? line : ' '}
        </Text>
      ))}
    </View>
  );
};

export const BulletList: FC<{
  text: string;
  marker: '+' | '-' | '−';
  markerColor?: string;
  primary?: string;
  styles: any;
}> = ({ text, marker, markerColor, primary, styles }) => {
  const textColor = primary || SQUAD_COLORS.text;
  return (
  <View>
    {String(text || '')
      .split('\n')
      .map((line) => line.replace(/^[-–−+]\s*/, '').trim())
      .filter(Boolean)
      .map((line, i) => (
        <View key={i} style={styles.bulletRow}>
          <Text style={{ ...styles.bulletMarker, color: markerColor || textColor }}>{marker}</Text>
          <Text style={{ ...styles.bulletText, color: textColor }}>{line}</Text>
        </View>
      ))}
  </View>
  );
};

/** react-pdf MiFID overlay — opaque white layer, justified notice text centered in content box. */
export const MifidOverlayLayerPdf: FC<{
  active: boolean;
  noticeText: string;
  children: React.ReactNode;
  minHeight?: number;
}> = ({ active, noticeText, children, minHeight }) => (
  <View
    style={{
      position: 'relative',
      width: '100%',
      ...(minHeight != null ? { minHeight } : null),
    }}
  >
    {children}
    {active ? (
      <View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#FFFFFF',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Text
          style={{
            width: SQUAD_MIFID_NOTICE_WIDTH_PCT,
            fontSize: SQUAD_TYPOGRAPHY_PDF.mifidOverlayNotice,
            lineHeight: 1.28,
            color: SQUAD_COLORS.text,
            textAlign: 'justify',
          }}
        >
          {noticeText}
        </Text>
      </View>
    ) : null}
  </View>
);
